import {
    Box, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Typography
} from "@mui/material";
import { useAsync2 } from "nate-react-api-helpers";
import { api } from "../../../api/API";
import { TaskFilter } from "../../../api/Tasks";
import { TeamMember } from "../../../api/Teams";
import { useTaskFeed } from "../../../misc/Feed";
import { PaletteGate } from "../../../misc/PaletteGate";
import { useDebounced } from "../../../misc/useDebounced";
import { useUrlState } from "../../../misc/urlParams";
import { teamName, useWorkspace } from "../../../misc/Workspace";
import { usePanels } from "../../panel/usePanels";
import { ProjectCode } from "../../project/ProjectCode";
import { TaskDueDate } from "../TaskDueDate";

type Status = "open" | "done" | "all";

// resultsPage is how many tasks one page of results holds
const resultsPage = 25;

// TaskSearchPage finds tasks by number, title or description, in the workspace. Every
// filter is in the url, so a link opens the same search.
export function TaskSearchPage() {
    const ws = useWorkspace();
    const url = useUrlState();

    const q = url.text("q");
    const project = url.id("project");
    const goal = url.id("goal");
    const dueFrom = url.text("dueFrom");
    const dueTo = url.text("dueTo");
    const status = (url.text("status") || "open") as Status;
    const assignee = url.id("assignee");
    const team = ws.team || url.id("team");
    const page = url.id("page");

    const search = useDebounced(q.trim(), 300);

    const filter: TaskFilter = {
        search: search || undefined,
        team: team || undefined,
        project: project || undefined,
        goal: goal || undefined,
        dueFrom: dueFrom || undefined,
        dueTo: dueTo || undefined,
        completed: status === "all" ? undefined : status === "done",
        assignedTo: assignee || undefined,
        limit: resultsPage,
        offset: page * resultsPage || undefined,
    };

    // the list is live, so a task edited from the panel shows the change in its row
    const list = useTaskFeed(filter);
    const panels = usePanels({ tasks: list.tasks });

    const projects = useAsync2(input => api.projects.list(input),
        { team: team || undefined, archived: false }, [team]);

    // the assignee filter lists the people on the teams being searched
    const teamIds = team ? [team] : ws.teams.map(row => row.id);
    const members = useAsync2(async (input: { teams: number[] }) => {
        const lists = await Promise.all(input.teams.map(id => api.teams.listMembers({ team: id })));
        const byUser = new Map<number, TeamMember>();
        lists.forEach(list => list.members.forEach(member => byUser.set(member.usr, member)));
        return Array.from(byUser.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, { teams: teamIds }, [teamIds.join(",")]);

    const goals = (projects.result || []).find(row => row.id === project)?.goals.filter(row => !row.archived) || [];

    // a change of filter starts again at the first page
    const set = (changes: Record<string, string>) => url.set({ ...changes, page: "" }, { replace: true });

    return (
        <PaletteGate>
            <Typography variant="h5">Search</Typography>
            <div style={{ height: 16 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <TextField size="small" label="Search" sx={{ flex: 1, minWidth: 240 }} autoFocus
                           placeholder="#123 or words in the title or description"
                           value={q} onChange={e => set({ q: e.target.value })} />

                <ToggleButtonGroup size="small" exclusive value={status}
                                   onChange={(_, value: Status | null) => value && set({ status: value === "open" ? "" : value })}>
                    <ToggleButton value="open">Open</ToggleButton>
                    <ToggleButton value="done">Done</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <div style={{ height: 8 }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {!ws.team && (
                    <TextField select size="small" label="Team" sx={{ minWidth: 160 }}
                               value={team || ""} onChange={e => set({ team: e.target.value, project: "", goal: "", assignee: "" })}>
                        <MenuItem value="">Any</MenuItem>
                        {ws.teams.map(row => (
                            <MenuItem key={row.id} value={row.id}>{teamName(row, row.id)}</MenuItem>
                        ))}
                    </TextField>
                )}

                <TextField select size="small" label="Project" sx={{ minWidth: 180 }}
                           value={project || ""} onChange={e => set({ project: e.target.value, goal: "" })}>
                    <MenuItem value="">Any</MenuItem>
                    {(projects.result || []).map(row => (
                        <MenuItem key={row.id} value={row.id}>{row.code} {row.name}</MenuItem>
                    ))}
                </TextField>

                {goals.length > 0 && (
                    <TextField select size="small" label="Goal" sx={{ minWidth: 160 }}
                               value={goal || ""} onChange={e => set({ goal: e.target.value })}>
                        <MenuItem value="">Any</MenuItem>
                        {goals.map(row => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
                    </TextField>
                )}

                <TextField select size="small" label="Assignee" sx={{ minWidth: 160 }}
                           value={assignee || ""} onChange={e => set({ assignee: e.target.value })}>
                    <MenuItem value="">Anyone</MenuItem>
                    {(members.result || []).map(row => <MenuItem key={row.usr} value={row.usr}>{row.name}</MenuItem>)}
                </TextField>

                <TextField type="date" size="small" label="Due from" slotProps={{ inputLabel: { shrink: true } }}
                           value={dueFrom} onChange={e => set({ dueFrom: e.target.value })} />
                <TextField type="date" size="small" label="Due to" slotProps={{ inputLabel: { shrink: true } }}
                           value={dueTo} onChange={e => set({ dueTo: e.target.value })} />
            </Box>

            <div style={{ height: 16 }} />
            {list.LoadingOrErrorElement}
            {projects.LoadingOrErrorElement}
            {members.LoadingOrErrorElement}
            {panels.LoadingOrErrorElement}

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell style={{ width: "100%" }}>Task</TableCell>
                            <TableCell>Project</TableCell>
                            <TableCell>Due</TableCell>
                            <TableCell>Assigned to</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.tasks.map(task => (
                            <TableRow key={task.id} hover style={{ cursor: "pointer" }}
                                      selected={task.id === panels.task?.id}
                                      onClick={() => panels.openTask(task)}>
                                <TableCell sx={{ color: "text.secondary" }}>{task.id}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                                        <span>{task.title}</span>
                                        <ProjectCode code={task.projectCode} color={task.projectColor}
                                                     done={!!task.completedAt} />
                                        {!ws.team && <Chip size="small" label={task.teamName} sx={{ height: 18, fontSize: 11 }} />}
                                    </Box>
                                </TableCell>
                                <TableCell style={{ whiteSpace: "nowrap" }}>{task.projectName}</TableCell>
                                <TableCell style={{ whiteSpace: "nowrap" }}>
                                    <TaskDueDate dueDate={task.dueDate} dueTime={task.dueTime}
                                                 completed={!!task.completedAt} />
                                </TableCell>
                                <TableCell style={{ whiteSpace: "nowrap" }}>
                                    {task.assignedTo.map(a => a.name).join(", ")}
                                </TableCell>
                                <TableCell>{task.completedAt ? "Done" : "Open"}</TableCell>
                            </TableRow>
                        ))}

                        {!list.loading && list.tasks.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">No matching tasks</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={list.total}
                    page={page}
                    rowsPerPage={resultsPage}
                    rowsPerPageOptions={[resultsPage]}
                    onPageChange={(_, value) => url.set({ page: value ? String(value) : "" }, { replace: true })}
                />
            </Paper>

            {panels.element}
        </PaletteGate>
    );
}
